import { MouseEventHandler, useCallback } from 'react';
import { useChannelActionContext, useChannelStateContext, useChatContext } from 'stream-chat-react';

import PushPinIcon from '@mui/icons-material/PushPin';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { ChannelInfoIcon } from './ChannelInfoIcon';
import { useWorkspaceController } from '../context/WorkspaceController';

import type { StreamChatType } from '../types';
import './ChatChannelHeader.scss';
import GGButton from '../../components/CommonComponents/GGButton/GGButton';
import { SessionManager } from '../../services/SessionManager/SessionManager';

// redux
import { useDispatch, useSelector } from 'react-redux'
import { openModal } from '../../redux/actionCard/paymentCard/paymentCardModalHandler'
import { Avatar, IconButton, Typography } from '@mui/material';
import { RootState } from '../../redux/store';

interface ChatChannelHeaderProps {
  onBackClick?: () => void;
  isMobile?: boolean;
}

export const ChatChannelHeader = ({ onBackClick, isMobile }: ChatChannelHeaderProps = {}) => {
  const { displayWorkspace } = useWorkspaceController();
  const { client } = useChatContext<StreamChatType>();
  const { channel, watcher_count } = useChannelStateContext<StreamChatType>();
  const { closeThread } = useChannelActionContext<StreamChatType>();
  const { togglePinnedMessageListOpen } = useWorkspaceController();
  // redux
  const dispatch = useDispatch()
  const isChatLocked = useSelector(
    (state: RootState) => state.chatLock.lockedChannels[channel.id ?? ""] === true
  );
  
  const teamHeader = `# ${channel?.data?.name || channel?.data?.id || 'random'}`;

  const openChannelEditPanel = useCallback(() => {
    displayWorkspace('Admin-Channel-Edit');
  }, [displayWorkspace]);

  const onPinIconClick: MouseEventHandler = useCallback((event) => {
    closeThread?.(event);
    togglePinnedMessageListOpen();
  }, [closeThread, togglePinnedMessageListOpen])

  const getMessagingHeader = () => {
    const members = Object.values(channel.state.members).filter(
      ({ user }) => user?.id !== client.userID,
    );
    const additionalMembers = members.length - 3;

    if (!members.length) {
      return (
        <div className='workspace-header__block'>
          {isMobile && onBackClick && (
            <IconButton onClick={onBackClick} className='mobile-back-button' aria-label="Back to messages">
              <ArrowBackIcon />
            </IconButton>
          )}
          <Avatar src='#' sx={{marginRight: '10px'}}/>
          <p className='team-channel-header__name user'>Johnny Blaze</p>
        </div>
      );
    }

    return (
      <div className='workspace-header__block'>
        {isMobile && onBackClick && (
          <IconButton onClick={onBackClick} className='mobile-back-button' aria-label="Back to messages">
            <ArrowBackIcon />
          </IconButton>
        )}
        {members.map(({ user }, i) => {
          if (i > 2) return null;
          return (
            <div key={i} className='workspace-header__block-item'>
              <Avatar alt={user?.name} src={user?.image} sx={{marginRight: '10px'}} >
                <Typography fontSize={'25px'}> {user?.name && user?.name[0]} </Typography> 
              </Avatar>
              <p className='team-channel-header__name user'>
                {user?.name || user?.id || 'Johnny Blaze'}
              </p>
            </div>
          );
        })}
        {additionalMembers > 0 && (
          <p className='team-channel-header__name user'>{`and ${additionalMembers} more`}</p>
        )}
      </div>
    );
  };

  const getWatcherText = (watchers?: number) => {
    if (!watchers || watchers <= 1) return 'Offline'; // no active users or only current user is active
    return 'Online';  // both users active 
  };

  const isOnline = (watchers?: number) => {
    return watchers && watchers > 1;
  };

  return (
    <div className='team-channel-header__container'>
      {channel.type === 'messaging' ? (
        getMessagingHeader()
      ) : (
        <div className='workspace-header__block'>
          <div className='team-channel-header__name workspace-header__title'>{teamHeader}</div>
          <button onClick={openChannelEditPanel}>
            <ChannelInfoIcon />
          </button>
        </div>
      )}
      <div className='workspace-header__block workspace-header__actions'>
        {channel.id?.split("_")[0] === SessionManager.shared.buyer?.id ? null : (
          
            <GGButton 
              className='create-new-order-btn' 
              startIcon={<NoteAddIcon/>} 
              onClick={() => dispatch(openModal())} 
              disabled={isChatLocked}
            >
            {isMobile ? 'New Order' : 'Create New Order'}
            </GGButton>
          
        )}
        <div className='workspace-header__subtitle status'>
          <div className={isOnline(watcher_count) ? 'activeBtn active' : 'activeBtn'}></div>
          {!isMobile && <span className="status-text">{getWatcherText(watcher_count)}</span>}
        </div>
        <button
          className='workspace-header__subtitle button'
          onClick={onPinIconClick}
        >
          <PushPinIcon className='pinIcon' />
          Pins
        </button>
      </div>
    </div>
  );
};
